import React from 'react';

const Section7ABTesting = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-amber-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">🧪</span>
      <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
        A/B Testing & Experimentation
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">Real experiments with statistical rigor, not guesswork</p>

    {/* Active experiments */}
    <div className="bg-gradient-to-br from-amber-50/50 to-orange-50/50 rounded-2xl p-6 border border-amber-200/30 mb-6">
      <h3 className="text-base font-bold text-amber-600 uppercase tracking-wider mb-5 text-center">Live Experiments</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[
          {
            name: 'Dream Wedding Generator',
            icon: '🪄',
            variants: [
              { id: 'A', name: 'Few-Shot', desc: 'Example themes guide output' },
              { id: 'B', name: 'Context-Aware', desc: 'RAG + user preferences injected' },
              { id: 'C', name: 'Hybrid Reasoning', desc: 'Chain-of-thought + few-shot + RAG' },
            ],
            metrics: 'Quality Score, Response Time, User Rating, Save Rate, Cost',
            sample: '99 total (33 per variant)'
          },
          {
            name: 'Moodboard Matcher',
            icon: '🎨',
            variants: [
              { id: 'A', name: 'Few-Shot', desc: 'Example analyses guide format' },
              { id: 'B', name: 'Theatrical', desc: 'Rich, evocative descriptions' },
              { id: 'C', name: 'Professional', desc: 'Concise, structured output' },
            ],
            metrics: 'Quality Score, Response Time, User Rating, Save Rate, Cost',
            sample: '99 total (33 per variant)'
          }
        ].map((exp, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-5 border border-amber-100/50">
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">{exp.icon}</span>
              <div>
                <h4 className="font-bold text-gray-800 text-sm">{exp.name}</h4>
                <p className="text-xs text-gray-400">Target: {exp.sample}</p>
              </div>
            </div>
            <div className="space-y-2 mb-3">
              {exp.variants.map((v, j) => (
                <div key={j} className="flex items-center bg-amber-50/50 rounded-lg px-3 py-2">
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white mr-2">{v.id}</span>
                  <div>
                    <span className="text-sm font-bold text-gray-800">{v.name}</span>
                    <span className="text-xs text-gray-400 ml-1">· {v.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200/50 pt-2">
              <p className="text-xs text-gray-400"><span className="font-bold">Metrics:</span> {exp.metrics}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Assignment & statistical methods */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
      {/* Assignment method */}
      <div className="bg-gradient-to-br from-amber-50/50 to-orange-50/50 rounded-2xl p-5 border border-amber-200/30">
        <h3 className="text-base font-bold text-amber-600 uppercase tracking-wider mb-4">Variant Assignment</h3>
        <div className="bg-white/80 rounded-xl p-4 border border-amber-100/50 mb-3">
          <p className="text-sm font-bold text-amber-700 mb-2">Round-Robin (not random)</p>
          <p className="text-sm text-gray-600 leading-relaxed mb-3">Server-side counter cycles A→B→C→A→B→C evenly. Guarantees equal distribution regardless of sample size.</p>
          <div className="bg-amber-50 rounded-lg px-3 py-2">
            <p className="text-xs font-mono text-amber-700">counter % variants.length → next variant</p>
          </div>
        </div>
        <div className="bg-white/80 rounded-xl p-4 border border-amber-100/50">
          <p className="text-sm font-bold text-amber-700 mb-2">Why not random?</p>
          <p className="text-sm text-gray-600 leading-relaxed">With small samples (99 users), random assignment produced uneven splits (12/22/20). Round-robin guarantees 33/33/33.</p>
        </div>
      </div>

      {/* Statistical methods */}
      <div className="bg-gradient-to-br from-orange-50/50 to-red-50/50 rounded-2xl p-5 border border-orange-200/30">
        <h3 className="text-base font-bold text-orange-600 uppercase tracking-wider mb-4">Statistical Analysis</h3>
        <div className="space-y-2">
          {[
            { method: 'Pooled t-test', purpose: 'Pairwise significance testing between all variant pairs (A vs B, B vs C, A vs C). p < 0.05 threshold.', icon: '📐' },
            { method: 'Cohen\'s d', purpose: 'Effect size measurement. Negligible (<0.2), Small (0.2), Medium (0.5), Large (0.8). Practical significance beyond p-values.', icon: '📏' },
            { method: 'Confidence Intervals', purpose: '95% CI per variant per metric. Shows range of true mean, not just point estimate.', icon: '📊' },
            { method: 'Sample Size Design', purpose: '33 per variant (99 total), designed for medium effect detection (d=0.5) with 3-variant comparison.', icon: '⚡' },
          ].map((m, i) => (
            <div key={i} className="bg-white/80 rounded-lg p-3 border border-orange-100/50 flex items-start">
              <span className="text-lg mr-2">{m.icon}</span>
              <div>
                <p className="text-sm font-bold text-gray-800">{m.method}</p>
                <p className="text-xs text-gray-500">{m.purpose}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Experiment health monitoring */}
    <div className="bg-gradient-to-r from-amber-50/80 to-orange-50/80 rounded-2xl p-5 border border-amber-200/30 mb-6">
      <h3 className="text-base font-bold text-amber-600 uppercase tracking-wider mb-3">🩺 Experiment Health Monitoring</h3>
      <p className="text-sm text-gray-500 mb-4">Automated checks run on every dashboard load to catch issues early</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { check: 'Traffic Distribution', desc: 'Alerts if any variant deviates >10% from expected share', icon: '🚦' },
          { check: 'Data Quality', desc: 'Detects missing metrics. Alerts if >10% of samples lack required fields', icon: '🔍' },
          { check: 'Velocity', desc: 'Tracks sample collection rate. Flags stalled experiments', icon: '📈' },
          { check: 'Early Trends', desc: 'Preliminary winner detection before full sample collected', icon: '🏁' },
        ].map((c, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-3 border border-amber-100/50 text-center">
            <span className="text-xl block mb-1">{c.icon}</span>
            <p className="text-sm font-bold text-gray-800">{c.check}</p>
            <p className="text-xs text-gray-400 mt-1">{c.desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Link to live dashboard */}
    <div className="text-center">
      <a href="/admin/ab-testing" target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 transform hover:scale-105 shadow-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-amber-300/50 hover:shadow-xl">
        <span className="mr-2">🧪</span>View Live A/B Testing Dashboard →
      </a>
    </div>
  </section>
);

export default Section7ABTesting;
